import CustomLoadingButton from 'Components/CustomLoadingButton'
import ProductTariffFieldError from 'Components/ProductTariffFieldErrors'
import ServiceProviderFieldErrors from 'Components/ServiceProviderFieldErrors'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import ProductTariffAllowedApiClients from 'Services/ProductManagement/Features/AllowedClient/Components/ProductTariffAllowedApiClients'
import {
  useCreateSingleCatalogMutation,
  useUpdateSingleCatalogMutation
} from 'Services/ProductManagement/Features/Catalog/catalogApi'
import { SELECTED_CATALOG_PARAMETER_KEY } from 'Services/ProductManagement/Features/Catalog/Hooks/useCatalogsWithParams'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import { useGetSalesChannelParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { TariffCatalogTabType } from 'Services/ProductManagement/Features/Tariff/types'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import { DRAWER_TAB_PARAM_KEY, IS_WALTTI_SERVICE_ENVIRONMENT } from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  name: [],
  description: [],
  salesChannel: [],
  catalogCode: []
}

interface TariffCatalogFormProps {
  catalog?: Catalog
  successBaseUrl: string
}

export function TariffCatalogForm({ catalog, successBaseUrl }: Readonly<TariffCatalogFormProps>) {
  const navigate = useNavigate()
  const { id: tariffId } = useParams()

  const isEdit = !!catalog

  const [createSingleCatalog, createStatus] = useCreateSingleCatalogMutation()
  const [updateSingleCatalog, updateStatus] = useUpdateSingleCatalogMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [catalogDetails, setCatalogDetails] = useState<Partial<Catalog>>((isEdit && catalog) || {})

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    catalogDetails?.api_client_read || []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    catalogDetails?.api_client_write || []
  )

  const {
    data: salesChannelParameterGroups = null,
    isFetching: isSalesChannelParameterGroupsFetching
  } = useGetSalesChannelParameterGroupQuery(undefined, {
    refetchOnMountOrArgChange: true
  })

  const salesChannelOptions =
    salesChannelParameterGroups?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []
  // Add empty option for sales channel
  salesChannelOptions.unshift({
    label: '-',
    value: ''
  })

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Catalog
  ) => {
    if (value !== undefined) {
      setCatalogDetails(prevState => ({ ...prevState, [field]: value }) as Catalog)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const catalogData = {
      ...catalogDetails,
      api_client_read: selectedReadClients,
      api_client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleCatalog({ ...catalogData, id: catalog.id })
      : createSingleCatalog({ ...catalogData, tariff_id: tariffId })
    mutation
      .unwrap()
      .then((catalogResponse: Catalog) => {
        SnackbarUtils.success(t`Sales channel successfully saved.`)
        if (IS_WALTTI_SERVICE_ENVIRONMENT) {
          navigate(
            appendQueryParamsToUrl(`${successBaseUrl}/${catalogResponse.id}/products/`, {
              [SELECTED_CATALOG_PARAMETER_KEY]: catalogResponse.id,
              [DRAWER_TAB_PARAM_KEY]: TariffCatalogTabType.DETAILS
            })
          )
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_CATALOG_PARAMETER_KEY]: catalogResponse.id,
              [DRAWER_TAB_PARAM_KEY]: TariffCatalogTabType.DETAILS
            })
          )
        }
      })

      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations, message } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error saving the sales channel.`)
        }
      })
  }

  const setValidationErrors = (violations: ProductTariffViolation[]) => {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[violation.field] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <WithLoader loading={isSalesChannelParameterGroupsFetching}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            <Box sx={{ display: 'flex' }}>
              <TextField
                sx={{ minWidth: 200, mr: 2 }}
                name="name"
                required
                value={catalogDetails?.name ?? ''}
                label={t`Name`}
                size="small"
                slotProps={{ htmlInput: { maxLength: 40 } }}
                error={fieldErrors.name.length > 0}
                onChange={e => handleChange(e.target.value, 'name')}
              />
              <TextField
                sx={{ minWidth: 200 }}
                name="catalog_code"
                required
                value={catalogDetails?.catalog_code ?? ''}
                label={t`Catalog code`}
                size="small"
                error={fieldErrors.catalogCode.length > 0}
                onChange={e => handleChange(e.target.value, 'catalog_code')}
              />
              <ServiceProviderFieldErrors
                error={fieldErrors.catalogCode}
                identifier="catalog_code"
              />
            </Box>
            <Box>
              <TextField
                sx={{ minWidth: 400 }}
                name="description"
                value={catalogDetails?.description ?? ''}
                label={t`Description`}
                size="small"
                rows={4}
                multiline={true}
                error={fieldErrors.description.length > 0}
                onChange={e => handleChange(e.target.value, 'description')}
              />
              <ServiceProviderFieldErrors
                error={fieldErrors.description}
                identifier="description"
              />
            </Box>
            <Box>
              <FormControl sx={{ minWidth: 200, mt: 2 }}>
                <InputLabel size="small">{t`Parameter group`}</InputLabel>
                <Select
                  name="parameter_group"
                  size="small"
                  label={t`Parameter group`}
                  value={catalogDetails?.sales_channel ?? []}
                  error={fieldErrors.salesChannel?.length > 0}
                  onChange={e => handleChange(e.target.value, 'sales_channel')}
                >
                  {salesChannelOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                <ProductTariffFieldError
                  error={fieldErrors.salesChannel}
                  identifier="sales_channel"
                />
              </FormControl>
            </Box>
            <Box sx={{ maxWidth: '750px', py: 2 }}>
              <ProductTariffAllowedApiClients
                selectedReadClients={selectedReadClients}
                selectedWriteClients={selectedWriteClients}
                setSelectedReadClients={setSelectedReadClients}
                setSelectedWriteClients={setSelectedWriteClients}
              />
            </Box>
            <Box my={2}>
              <CustomLoadingButton
                isLoading={isSubmitLoading}
                type="submit"
                size="large"
                iconPosition="end"
              >
                {t`Save sales channel`}
              </CustomLoadingButton>
            </Box>
          </StyledForm>
        </WithLoader>
      </Grid>
    </Grid>
  )
}
